import { Provider as LocaleProvider } from 'lib/locale-context';
import localization from 'lib/localization';
import useBodyOSClasses from 'lib/use-body-os-classes';
import { Localization } from '@bumble/localization/components';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import React, { useEffect } from 'react';
import supportedLanguageIds, { SupportedLanguage } from 'lib/lexemes/supported-languages';
import { AppError } from 'lib/app-error';
import PageSeo from 'components/join-hives/page-seo';
import '../styles/index.scss';
import { useGelatoLogger } from 'lib/use-gelato-logger';

function App({ Component, router, pageProps }: AppProps) {
    const lang = getDefaultLanguage(pageProps.lang);
    const lexemes = getLang(lang);

    useBodyOSClasses();

    useLazyLoadCss();

    useGelatoLogger();

    const localisedContent = (
        <Localization
            instance={localization}
            lang={lang}
            entries={lexemes.lexemes}
            commonWords={lexemes.common}
        >
            <PageSeo lang={lang} router={router} />
            <Component {...pageProps} />
        </Localization>
    );

    return (
        <LocaleProvider value={lang}>
            <Head>
                <meta name="viewport" content="initial-scale=1.0, width=device-width" />
                <meta name="robots" content="noindex,nofollow" />
                <meta name="googlebot" content="noindex,nofollow" />
                <meta name="google" content="nositelinkssearchbox" />

                <link
                    id="deferred-styles"
                    rel="preload"
                    href="/bff/join-plans/fonts/Circular20/fonts-circular20.css"
                    as="style"
                />
            </Head>
            {localisedContent}
        </LocaleProvider>
    );
}

export default App;

function getDefaultLanguage(lang: string): SupportedLanguage {
    if (supportedLanguageIds.includes(lang as SupportedLanguage)) {
        return lang as SupportedLanguage;
    } else if (typeof lang === 'string') {
        // We have a language being passed, but it's not valid, it means user is on an invalid url
        // e.g. /xx/the-buzz or team.bumble.com/xx/open-roles
        throw new AppError({
            statusCode: 404,
            message: `Invalid page language - ${lang}`,
        });
    }

    return 'en';
}

function getLang(lang: SupportedLanguage) {
    return require(`lib/lexemes/${lang}`).default;
}

/**
 * Uses "Google approved" way of lazy loading CSS
 * 'deferred-styles' is a noscript tag embedded in the page which the browser doesn't execute for normal environments
 * We just read it and re-add it to the body
 */
function useLazyLoadCss() {
    useEffect(() => {
        const addStylesNode = global.document.getElementById('deferred-styles');
        if (!addStylesNode) {
            return;
        }

        const linkEl = global.document.createElement('link');
        linkEl.rel = 'stylesheet';
        linkEl.href = addStylesNode.getAttribute('href') || '';
        global.document.body.appendChild(linkEl);
    }, []);
}
